export interface CodeSample {
  name: string
  description: string
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced'
  concepts: string[]
  code: {
    Python: string
    JavaScript: string
    Rust: string
    TypeScript?: string
    Java?: string
    Go?: string
    Swift?: string
    Ruby?: string
    'C++'?: string
    C?: string
  }
}

export const codeSamples: Record<string, CodeSample> = {
  fibonacci: {
    name: 'Fibonacci Sequence',
    description: 'Recursive function with mathematical logic',
    difficulty: 'Beginner',
    concepts: ['recursion', 'base cases', 'mathematical sequences'],
    code: {
      Python: `def fibonacci(n):
    if n <= 1:
        return n
    return fibonacci(n-1) + fibonacci(n-2)

# Calculate first 10 numbers
for i in range(10):
    print(fibonacci(i))`,
      JavaScript: `function fibonacci(n) {
    if (n <= 1) return n;
    return fibonacci(n - 1) + fibonacci(n - 2);
}

// Calculate first 10 numbers
for (let i = 0; i < 10; i++) {
    console.log(fibonacci(i));
}`,
      TypeScript: `function fibonacci(n: number): number {
    if (n <= 1) return n;
    return fibonacci(n - 1) + fibonacci(n - 2);
}

// Calculate first 10 numbers
for (let i: number = 0; i < 10; i++) {
    console.log(fibonacci(i));
}`,
      Rust: `fn fibonacci(n: u32) -> u32 {
    match n {
        0 | 1 => n,
        _ => fibonacci(n - 1) + fibonacci(n - 2)
    }
}

fn main() {
    for i in 0..10 {
        println!("{}", fibonacci(i));
    }
}`
    }
  },
  arrayOps: {
    name: 'Array Operations',
    description: 'Map, filter, and reduce patterns',
    difficulty: 'Beginner',
    concepts: ['arrays', 'iteration', 'functional programming'],
    code: {
      Python: `numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

# Filter even numbers
evens = [x for x in numbers if x % 2 == 0]

# Square each number
squares = [x ** 2 for x in evens]

# Sum all squares
total = sum(squares)
print(f"Sum of even squares: {total}")`,
      JavaScript: `const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Filter even numbers and square them
const evenSquares = numbers
    .filter(x => x % 2 === 0)
    .map(x => x ** 2);

// Sum all squares
const total = evenSquares.reduce((sum, x) => sum + x, 0);
console.log(\`Sum of even squares: \${total}\`);`,
      TypeScript: `const numbers: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Filter even numbers and square them
const evenSquares: number[] = numbers
    .filter((x: number) => x % 2 === 0)
    .map((x: number) => x ** 2);

// Sum all squares
const total: number = evenSquares.reduce((sum: number, x: number) => sum + x, 0);
console.log(\`Sum of even squares: \${total}\`);`,
      Rust: `fn main() {
    let numbers: Vec<i32> = (1..=10).collect();

    let total: i32 = numbers
        .iter()
        .filter(|&&x| x % 2 == 0)
        .map(|&x| x * x)
        .sum();

    println!("Sum of even squares: {}", total);
}`
    }
  },
  asyncData: {
    name: 'Async Data Fetching',
    description: 'Promises, async/await, and error handling',
    difficulty: 'Intermediate',
    concepts: ['async programming', 'promises', 'error handling'],
    code: {
      Python: `import asyncio
import aiohttp

async def fetch_user_data(user_id):
    async with aiohttp.ClientSession() as session:
        try:
            async with session.get(f'/api/users/{user_id}') as response:
                if response.status == 200:
                    return await response.json()
                else:
                    raise Exception(f"HTTP {response.status}")
        except Exception as e:
            print(f"Error fetching user {user_id}: {e}")
            return None

# Usage
async def main():
    user = await fetch_user_data(123)
    if user:
        print(f"User: {user['name']}")`,
      JavaScript: `async function fetchUserData(userId) {
    try {
        const response = await fetch(\`/api/users/\${userId}\`);

        if (!response.ok) {
            throw new Error(\`HTTP \${response.status}\`);
        }

        const user = await response.json();
        return user;
    } catch (error) {
        console.error(\`Error fetching user \${userId}:\`, error);
        return null;
    }
}

// Usage
fetchUserData(123).then(user => {
    if (user) {
        console.log(\`User: \${user.name}\`);
    }
});`,
      TypeScript: `interface User {
    id: number;
    name: string;
    email: string;
}

async function fetchUserData(userId: number): Promise<User | null> {
    try {
        const response: Response = await fetch(\`/api/users/\${userId}\`);

        if (!response.ok) {
            throw new Error(\`HTTP \${response.status}\`);
        }

        const user: User = await response.json();
        return user;
    } catch (error) {
        console.error(\`Error fetching user \${userId}:\`, error);
        return null;
    }
}

// Usage
fetchUserData(123).then((user: User | null) => {
    if (user) {
        console.log(\`User: \${user.name}\`);
    }
});`,
      Rust: `use reqwest;
use serde_json::Value;

async fn fetch_user_data(user_id: u32) -> Result<Value, Box<dyn std::error::Error>> {
    let url = format!("https://api.example.com/users/{}", user_id);

    let response = reqwest::get(&url).await?;

    if response.status().is_success() {
        let user: Value = response.json().await?;
        Ok(user)
    } else {
        Err(format!("HTTP {}", response.status()).into())
    }
}

#[tokio::main]
async fn main() {
    match fetch_user_data(123).await {
        Ok(user) => println!("User: {}", user["name"]),
        Err(e) => eprintln!("Error: {}", e),
    }
}`
    }
  },
  classObjects: {
    name: 'Classes & Objects',
    description: 'Object-oriented programming concepts',
    difficulty: 'Intermediate',
    concepts: ['classes', 'inheritance', 'encapsulation'],
    code: {
      Python: `class Animal:
    def __init__(self, name, species):
        self.name = name
        self.species = species

    def speak(self):
        return f"{self.name} makes a sound"

class Dog(Animal):
    def __init__(self, name, breed):
        super().__init__(name, "Canine")
        self.breed = breed

    def speak(self):
        return f"{self.name} barks!"

# Usage
dog = Dog("Buddy", "Golden Retriever")
print(dog.speak())
print(f"Breed: {dog.breed}")`,
      JavaScript: `class Animal {
    constructor(name, species) {
        this.name = name;
        this.species = species;
    }

    speak() {
        return \`\${this.name} makes a sound\`;
    }
}

class Dog extends Animal {
    constructor(name, breed) {
        super(name, "Canine");
        this.breed = breed;
    }

    speak() {
        return \`\${this.name} barks!\`;
    }
}

// Usage
const dog = new Dog("Buddy", "Golden Retriever");
console.log(dog.speak());
console.log(\`Breed: \${dog.breed}\`);`,
      TypeScript: `class Animal {
    constructor(public name: string, public species: string) {}

    speak(): string {
        return \`\${this.name} makes a sound\`;
    }
}

class Dog extends Animal {
    constructor(name: string, public breed: string) {
        super(name, "Canine");
    }

    speak(): string {
        return \`\${this.name} barks!\`;
    }
}

// Usage
const dog: Dog = new Dog("Buddy", "Golden Retriever");
console.log(dog.speak());
console.log(\`Breed: \${dog.breed}\`);`,
      Rust: `trait Animal {
    fn name(&self) -> &str;
    fn species(&self) -> &str;
    fn speak(&self) -> String {
        format!("{} makes a sound", self.name())
    }
}

struct Dog {
    name: String,
    breed: String,
}

impl Animal for Dog {
    fn name(&self) -> &str {
        &self.name
    }

    fn species(&self) -> &str {
        "Canine"
    }

    fn speak(&self) -> String {
        format!("{} barks!", self.name)
    }
}

fn main() {
    let dog = Dog {
        name: "Buddy".to_string(),
        breed: "Golden Retriever".to_string(),
    };

    println!("{}", dog.speak());
    println!("Breed: {}", dog.breed);
}`
    }
  },
  errorHandling: {
    name: 'Error Handling',
    description: 'Try-catch patterns and error management',
    difficulty: 'Intermediate',
    concepts: ['exceptions', 'error handling', 'defensive programming'],
    code: {
      Python: `import json
from typing import Optional

def parse_config(file_path: str) -> Optional[dict]:
    try:
        with open(file_path, 'r') as file:
            config = json.load(file)

        # Validate required fields
        if 'database' not in config:
            raise ValueError("Missing required 'database' field")

        return config

    except FileNotFoundError:
        print(f"Config file not found: {file_path}")
        return None
    except json.JSONDecodeError as e:
        print(f"Invalid JSON in config: {e}")
        return None
    except ValueError as e:
        print(f"Config validation error: {e}")
        return None

# Usage
config = parse_config("app.json")
if config:
    print("Config loaded successfully")`,
      JavaScript: `function parseConfig(filePath) {
    try {
        const fs = require('fs');
        const fileContent = fs.readFileSync(filePath, 'utf8');
        const config = JSON.parse(fileContent);
        
        // Validate required fields
        if (!config.database) {
            throw new Error("Missing required 'database' field");
        }
        
        return config;
        
    } catch (error) {
        if (error.code === 'ENOENT') {
            console.error(\`Config file not found: \${filePath}\`);
        } else if (error instanceof SyntaxError) {
            console.error(\`Invalid JSON in config: \${error.message}\`);
        } else {
            console.error(\`Config validation error: \${error.message}\`);
        }
        return null;
    }
}

// Usage
const config = parseConfig('app.json');
if (config) {
    console.log('Config loaded successfully');
}`,
      TypeScript: `interface Config {
    database: {
        host: string;
        port: number;
    };
    [key: string]: any;
}

function parseConfig(filePath: string): Config | null {
    try {
        const fs = require('fs');
        const fileContent: string = fs.readFileSync(filePath, 'utf8');
        const config: Config = JSON.parse(fileContent);

        // Validate required fields
        if (!config.database) {
            throw new Error("Missing required 'database' field");
        }

        return config;

    } catch (error: any) {
        if (error.code === 'ENOENT') {
            console.error(\`Config file not found: \${filePath}\`);
        } else if (error instanceof SyntaxError) {
            console.error(\`Invalid JSON in config: \${error.message}\`);
        } else {
            console.error(\`Config validation error: \${error.message}\`);
        }
        return null;
    }
}

// Usage
const config: Config | null = parseConfig('app.json');
if (config) {
    console.log('Config loaded successfully');
}`,
      Rust: `use std::fs;
use serde_json::{self, Value};

#[derive(Debug)]
enum ConfigError {
    FileNotFound,
    InvalidJson(String),
    ValidationError(String),
}

fn parse_config(file_path: &str) -> Result<Value, ConfigError> {
    // Read file
    let content = fs::read_to_string(file_path)
        .map_err(|_| ConfigError::FileNotFound)?;
    
    // Parse JSON
    let config: Value = serde_json::from_str(&content)
        .map_err(|e| ConfigError::InvalidJson(e.to_string()))?;
    
    // Validate required fields
    if !config.get("database").is_some() {
        return Err(ConfigError::ValidationError(
            "Missing required 'database' field".to_string()
        ));
    }
    
    Ok(config)
}

fn main() {
    match parse_config("app.json") {
        Ok(_) => println!("Config loaded successfully"),
        Err(ConfigError::FileNotFound) => {
            eprintln!("Config file not found");
        },
        Err(ConfigError::InvalidJson(msg)) => {
            eprintln!("Invalid JSON: {}", msg);
        },
        Err(ConfigError::ValidationError(msg)) => {
            eprintln!("Validation error: {}", msg);
        },
    }
}`
    }
  }
}